import {BreadcrumbType, Form, FormSubmitHandler} from 'platform/components';
import {useLocale} from 'platform/locale';
import {match} from 'ts-pattern';
import {object} from 'yup';

import {prop} from 'ramda';

import {TemplateTypeEnum, useCreateTemplateMutation} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useTenant} from '@dms/shared';

import {ApiError, useNavigate, yupString} from 'shared/web';

import {MessageTemplateFormValues} from '../types/MessageTemplateFormValues';
import {getContextOptions} from '../utils/getContextOptions';
import {MessageTemplateFields} from './MessageTemplateFields';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.messageTemplates.labels.list'),
    href: settingsRoutes.messageTemplates,
  },
];

type Props = {
  templateType: TemplateTypeEnum;
  messageTemplateId?: string;
};

export const CreateMessageTemplateForm = (props: Props) => {
  const {language} = useLocale();
  const {isMultiLocale, tenant} = useTenant();
  const navigate = useNavigate();

  const [createTemplateMutation, {isLoading: isMutationInProgress}] = useCreateTemplateMutation();

  const onSubmit: FormSubmitHandler<MessageTemplateFormValues> = async (
    data: MessageTemplateFormValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await createTemplateMutation({
      templateType: props.templateType,
      createTemplateRequestBody: {
        ...data,
        locale: data.locale ?? tenant?.defaultLocale ?? language,
      },
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.messageTemplates);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const schemaSMS = object({
    name: yupString.required(),
    context: yupString.required().oneOf(getContextOptions(props.templateType).map(prop('value'))),
    body: yupString.required(),
    ...(isMultiLocale && {locale: yupString.required()}),
  });

  const schemaChat = schemaSMS;

  const schemaEmail = schemaSMS.shape({
    subject: yupString.required(),
  });

  const defaultValues: Partial<MessageTemplateFormValues> | undefined = isMultiLocale
    ? {locale: tenant?.defaultLocale}
    : undefined;

  return (
    <SettingsTemplate
      header={{
        breadcrumbs,
        title: i18n.t('page.messageTemplates.labels.new', {
          templateType: i18n.t(`page.messageTemplates.labels.${props.templateType}`),
        }),
      }}
      description={i18n.t('page.messageTemplates.labels.newDescription')}
      data-testid={testIds.settings.messageTemplates(`${props.templateType}new`)}
    >
      <Form<MessageTemplateFormValues>
        schema={match(props.templateType)
          .with('sms', () => schemaSMS)
          .with('chat', () => schemaChat)
          .with('email', () => schemaEmail)
          .exhaustive()}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi, navigateWithoutBlocker) => (
          <MessageTemplateFields
            formApi={formApi}
            control={control}
            templateType={props.templateType}
            isMutationInProgress={isMutationInProgress}
            onDiscard={() => navigateWithoutBlocker(settingsRoutes.messageTemplates)}
          />
        )}
      </Form>
    </SettingsTemplate>
  );
};
